import { useEffect, useState } from 'react'
import { api, ApiError, getAuthToken } from '../../services/api'
import { PageHeader } from '../../components/ui/PageHeader'
import { FormField } from '../../components/ui/FormField'
import { card, input, btnPrimary, colors } from '../../components/ui/styles'

interface CustomerRow {
  readonly id: string
  readonly name: string
}

interface SiteRow {
  readonly id: string
  readonly name: string
  readonly udmSiteId: number
}

interface TemplateRow {
  readonly id: string
  readonly key: string
  readonly displayName: string
  readonly deviceType: string
  readonly protocol: string
}

interface ProvisionResult {
  readonly device: { id: string; udmDeviceId: number; name: string }
  readonly config: { id: string; configVersion: number }
}

export default function DeviceProvisioningPage() {
  const [customers, setCustomers] = useState<readonly CustomerRow[]>([])
  const [sites, setSites] = useState<readonly SiteRow[]>([])
  const [templates, setTemplates] = useState<readonly TemplateRow[]>([])
  const [customerId, setCustomerId] = useState('')
  const [siteId, setSiteId] = useState('')
  const [templateKey, setTemplateKey] = useState('')
  const [deviceName, setDeviceName] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [port, setPort] = useState('')
  const [overrideText, setOverrideText] = useState('')
  const [addToLive, setAddToLive] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ProvisionResult | null>(null)

  useEffect(() => {
    void api.get<readonly CustomerRow[]>('/fleet/customers').then((r) => setCustomers(r.data ?? []))
    void api.get<readonly TemplateRow[]>('/fleet/device-templates').then((r) => setTemplates(r.data ?? []))
  }, [])

  useEffect(() => {
    setSiteId('')
    setSites([])
    if (!customerId) return
    void api.get<readonly SiteRow[]>(`/fleet/customers/${customerId}/sites`).then((r) => setSites(r.data ?? []))
  }, [customerId])

  async function handleProvision() {
    setSaving(true)
    setError(null)
    setResult(null)
    try {
      let overrideJson: Record<string, unknown> | undefined
      if (overrideText.trim()) {
        const parsed: unknown = JSON.parse(overrideText)
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          throw new Error('Override must be a JSON object')
        }
        overrideJson = parsed as Record<string, unknown>
      }
      const res = await api.post<ProvisionResult>(`/fleet/sites/${siteId}/devices/provision`, {
        templateKey,
        deviceName,
        fields: {
          DeviceName: deviceName,
          Connection: { Mode: 'Client', IpAddress: ipAddress, Port: Number(port) },
        },
        overrideJson,
        addToLive,
      })
      if (!res.data) throw new Error('No data returned')
      setResult(res.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Provision failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleDownload() {
    setError(null)
    try {
      const res = await fetch(`/api/fleet/sites/${siteId}/provisioning/appsettings`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${getAuthToken() ?? ''}` },
      })
      if (!res.ok) throw new Error(`Download failed (${res.status})`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'appsettings.site.json'
      link.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed')
    }
  }

  const canSubmit = !!siteId && !!templateKey && !!deviceName && !!ipAddress && !!port && !saving

  return (
    <div>
      <PageHeader title="Provision Instrument" subtitle="Pre-register a device and generate its initial UDM config" />
      <div style={card}>
        <FormField name="customer" label="Customer">
          <select
            id="customer"
            aria-label="Customer"
            style={input}
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          >
            <option value="">-- Select customer --</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FormField>

        <FormField name="site" label="Site">
          <select
            id="site"
            aria-label="Site"
            style={input}
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            disabled={!customerId}
          >
            <option value="">-- Select site --</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </FormField>

        <FormField name="template" label="Template">
          <select
            id="template"
            aria-label="Template"
            style={input}
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
          >
            <option value="">-- Select template --</option>
            {templates.map((t) => (
              <option key={t.id} value={t.key}>
                {t.displayName} ({t.protocol})
              </option>
            ))}
          </select>
        </FormField>

        <FormField name="deviceName" label="Device Name" value={deviceName} onChange={setDeviceName} />

        <FormField name="ipAddress" label="IP Address" value={ipAddress} onChange={setIpAddress} />

        <FormField name="port" label="Port" type="number" value={port} onChange={setPort} />

        <FormField name="override" label="Advanced override JSON (optional)">
          <textarea
            id="override"
            aria-label="Advanced override JSON (optional)"
            style={{ ...input, minHeight: '80px', fontFamily: 'monospace' }}
            value={overrideText}
            onChange={(e) => setOverrideText(e.target.value)}
          />
        </FormField>

        <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0' }}>
          <input type="checkbox" checked={addToLive} onChange={(e) => setAddToLive(e.target.checked)} />
          Add to live site config (takes effect after UDM Phase 2)
        </label>

        {error && <div style={{ color: colors.danger, marginTop: '0.5rem' }}>{error}</div>}

        {result && (
          <div style={{ color: colors.success, marginTop: '0.5rem' }}>
            Provisioned device {result.device.name} (alias {result.device.udmDeviceId}), config v
            {result.config.configVersion}.
            <div style={{ marginTop: '0.5rem' }}>
              <button onClick={() => void handleDownload()} style={btnPrimary} type="button">
                Download appsettings.site.json
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="button" onClick={() => void handleProvision()} style={btnPrimary} disabled={!canSubmit}>
            {saving ? 'Provisioning...' : 'Provision'}
          </button>
        </div>
      </div>
    </div>
  )
}
